<template>
    <div class="order-check">
        <div class="sticky-top">
            <div>
                <DCard padding>
                    <div class="overview">
                        <main>
                            <div class="more-line">
                                <span>{{ __('orders.check.status') }}</span>
                                <span>
                                    <DTag :label="__('orders.status_'+data.status)" />
                                </span>
                            </div>
                            <div class="more-line">
                                <span>{{ __('orders.check.contract_manager') }}</span>
                                <span>
                                    {{ formatString(orderAdditionalInfo.contract_manager) }}
                                </span>
                            </div>
                            <div class="more-line">
                                <span>{{ __('orders.check.screening_result') }}</span>
                                <span>
                                    {{ orderAdditionalInfo.screening_result
                                        ? __('orders.check.'+orderAdditionalInfo.screening_result)
                                        : formatString(orderAdditionalInfo.screening_result ?? null) }}
                                </span>
                            </div>
                            <div class="more-line-big-width-second-bold">
                                <span>{{ __('orders.check.order_number') }}</span>
                                <span>{{ data.orderNumber }}</span>
                            </div>
                            <div class="more-line">
                                <span />
                                <span />
                            </div>
                        </main>
                        <aside>
                            <div class="more-line">
                                <span />
                                <span v-if="signedDocumentUrl !== null">
                                    <a
                                        href="javascript:void(0);"
                                        @click="openSignedDocument(signedDocumentUrl)"
                                    >
                                        {{ __('orders.check.order_document') }}
                                    </a>
                                </span>
                                <span v-else>
                                    {{ __('orders.check.order_document') }}
                                </span>
                            </div>
                        </aside>
                    </div>
                    <div class="overview">
                        <main>
                            <div class="more-line">
                                <span>{{ __('orders.check.priority') }} <DIcon
                                    v-if="userPermissions.includes('order.view')"
                                    name="pencil"
                                    class="cursor-pointer"
                                    @click="setPriority"
                                /></span>
                                <span>
                                    {{ formatString(orderAdditionalInfo.priority ?? null) }}
                                </span>
                            </div>
                            <div class="more-line">
                                <span>{{ __('orders.check.order_date') }}</span>
                                <span>{{ setDateFormat(data.orderData.created_at, 'YYYY-MM-DD', '/') }}</span>
                            </div>
                            <div class="more-line">
                                <span>{{ __('orders.check.entry_date') }}</span>
                                <span>{{ setDateFormat(data.orderData.finalized_at, 'YYYY-MM-DD', '/') }}</span>
                            </div>
                            <div class="more-line-big-width">
                                <span>{{ __('orders.check.sales_agent_name') }}</span>
                                <span>{{ formatString(data.salesAgent.firstname ?? null ) }} {{ formatString(data.salesAgent.lastname ?? null) }}</span>
                            </div>
                            <div class="more-line">
                                <span>{{ __('orders.check.sales_agent_no') }}</span>
                                <span>{{ formatString(data.salesAgent.external_id ?? null) }}</span>
                            </div>
                        </main>
                        <aside>
                            <div class="more-line">
                                <span />
                                <span />
                            </div>
                        </aside>
                    </div>
                    <div class="overview">
                        <main>
                            <div class="more-line">
                                <span>{{ __('orders.check.credit_rating') }} <DIcon
                                    v-if="userPermissions.includes('order.view')"
                                    name="pencil"
                                    class="cursor-pointer"
                                    @click="setCreditRating"
                                /></span>
                                <span>{{ formatString(orderAdditionalInfo.credit_rating ?? null) }}</span>
                            </div>
                            <div class="more-line">
                                <span>{{ __('orders.check.customer_number') }}</span>
                                <span v-if="data.valid && data.clientCustomerId !== null">
                                    {{ data.clientCustomerId }}
                                </span>
                                <span v-else>
                                    {{ formatString('') }}
                                </span>
                            </div>
                            <div class="more-line">
                                <span>{{ __('orders.check.sid') }}</span>
                                <span
                                    v-if="data.valid && data.customer"
                                >
                                    {{ data.customer.herold_location_id }}
                                </span>
                                <span
                                    v-else
                                >
                                    {{ formatString('') }}
                                </span>
                            </div>
                            <div class="more-line-big-width">
                                <span>{{ __('orders.check.company') }}</span>
                                <span
                                    v-if="data.valid && data.customer"
                                >
                                    {{ data.customer.company }}
                                </span>
                                <span
                                    v-else
                                >
                                    {{ formatString('') }}
                                </span>
                            </div>
                            <div class="more-line">
                                <span />
                                <span />
                            </div>
                        </main>
                        <aside>
                            <div class="more-line">
                                <span />
                                <span v-if="data.valid && data.clientCustomerId !== null">
                                    <Link :href="$route('customers.customers.show', {customer: data.clientCustomerId})">
                                        {{ __('orders.check.customer_profile') }}
                                    </Link>
                                </span>
                                <span v-else>
                                    {{ __('orders.check.customer_profile') }}
                                </span>
                            </div>
                        </aside>
                    </div>
                </DCard>
                <DCard padding>
                    <div class="pb-2">
                        <DButton
                            mini
                            :icon="showComments > 0 ? 'arrow-down' : 'arrow-up'"
                            @click="showComments = !showComments"
                        /> {{ __('orders.check.comments') }}
                    </div>
                    <div class="overview">
                        <main>
                            <div
                                v-show="showComments"
                                class="more-line-fourth"
                            >
                                <span>{{ __('orders.check.internal_comment') }} <DIcon
                                    v-if="userPermissions.includes('order.view')"
                                    name="pencil"
                                    class="cursor-pointer"
                                    @click="setInternalComment"
                                /></span>
                                <span>{{ __('orders.check.external_comment') }} <DIcon
                                    v-if="userPermissions.includes('order.view')"
                                    name="pencil"
                                    class="cursor-pointer"
                                    @click="setExternalComment"
                                /></span>
                            </div>
                            <div
                                v-show="showComments"
                                class="more-line-comments"
                            >
                                <span>
                                    <DFormTextarea
                                        id="internal_comment"
                                        :value="orderAdditionalInfo.internal_comment ?? null"
                                        disabled
                                        rows="1"
                                        maxlength="255"
                                    />
                                </span>
                                <span>
                                    <DFormTextarea
                                        id="external_comment"
                                        :value="orderAdditionalInfo.external_comment ?? null"
                                        disabled
                                        rows="1"
                                        maxlength="255"
                                    />
                                </span>
                            </div>
                        </main>
                    </div>
                </DCard>
                <div class="tabs-wrapper">
                    <DTabs>
                        <DTabItem
                            v-if="userPermissions.includes('order.view')"
                            :active="['orders.psa.automatic-checks'].includes($route().current())"
                            :label="__('orders.check.automatic_checks')"
                            :route-url="$route('orders.psa.automatic-checks', {order: data.orderNumber, client: 'PSA'})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('order.view')"
                            :active="['orders.psa.manual-checks'].includes($route().current())"
                            :label="__('orders.check.manual_checks')"
                            :route-url="$route('orders.psa.manual-checks', {order: data.orderNumber, client: 'PSA'})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('order.view')"
                            :active="['orders.psa.attachments'].includes($route().current())"
                            :label="__('orders.check.attachments')"
                            :route-url="$route('orders.psa.attachments', {order: data.orderNumber, client: 'PSA'})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('order.view')"
                            :active="['orders.psa.clarifications'].includes($route().current())"
                            :label="__('orders.check.clarifications', {open_clarifications: orderAdditionalInfo?.open_clarifications ?? 0})"
                            :route-url="$route('orders.psa.clarifications', {order: data.orderNumber, client: 'PSA'})"
                        />
                        <DTabItem
                            v-if="userPermissions.includes('order.view')"
                            :active="['orders.psa.history'].includes($route().current())"
                            :label="__('orders.check.history')"
                            :route-url="$route('orders.psa.history', {order: data.orderNumber, client: 'PSA'})"
                        />
                    </DTabs>
                </div>
            </div>
        </div>
        <PsaAdditionalInfoModal
            v-if="showAdditionalInfoModal"
            :title="additionalInfoTitle"
            :form="additionalInfoForm"
            :label="additionalInfoLabel"
            :input-type="additionalInfoInputType"
            :order="data"
            :show-modal="showAdditionalInfoModal"
            @close-modal="closeAdditionalInfoModal"
        />
        <slot />
    </div>
</template>

<script>

import PsaAdditionalInfoModal from '~@/Components/Orders/Psa/PsaAdditionalInfo'
export default {
    components: { PsaAdditionalInfoModal },
    props: {
        data: {
            type: Object,
            default: () => { return {} }
        },
        orderAdditionalInfo: {
            type: Object,
            default: () => { return {} }
        }
    },
    data () {
        return {
            showComments: !(this.orderAdditionalInfo.internal_comment === null && this.orderAdditionalInfo.external_comment === null),
            showAdditionalInfoModal: false,
            additionalInfoForm: null,
            additionalInfoTitle: null,
            additionalInfoLabel: null,
            additionalInfoInputType: null
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        signedDocumentUrl () {
            let documentUrl = null

            this.data.documents.forEach((document) => {
                if (document.signed === true) {
                    documentUrl = document.url
                }
            })

            return documentUrl
        }
    },
    methods: {
        setPriority () {
            this.showAdditionalInfoModal = true
            this.additionalInfoTitle = this.__('orders.check.change_priority_modal_title')
            this.additionalInfoForm = this.$inertia.form({
                priority: this.orderAdditionalInfo.priority ?? null,
                field: 'priority'
            })
            this.additionalInfoInputType = 'number'
            this.additionalInfoLabel = this.__('orders.check.priority')
        },
        setInternalComment () {
            this.showAdditionalInfoModal = true
            this.additionalInfoTitle = this.__('orders.check.change_internal_comment_modal_title')
            this.additionalInfoForm = this.$inertia.form({
                internal_comment: this.orderAdditionalInfo.internal_comment ?? null,
                field: 'internal_comment'
            })
            this.additionalInfoInputType = 'textarea'
            this.additionalInfoLabel = this.__('orders.check.internal_comment')
        },
        setExternalComment () {
            this.showAdditionalInfoModal = true
            this.additionalInfoTitle = this.__('orders.check.change_external_comment_modal_title')
            this.additionalInfoForm = this.$inertia.form({
                external_comment: this.orderAdditionalInfo.external_comment ?? null,
                field: 'external_comment'
            })
            this.additionalInfoInputType = 'textarea'
            this.additionalInfoLabel = this.__('orders.check.external_comment')
        },
        setCreditRating () {
            this.showAdditionalInfoModal = true
            this.additionalInfoTitle = this.__('orders.check.change_credit_rating_modal_title')
            this.additionalInfoForm = this.$inertia.form({
                credit_rating: this.orderAdditionalInfo.credit_rating ?? null,
                field: 'credit_rating'
            })
            this.additionalInfoInputType = 'string'
            this.additionalInfoLabel = this.__('orders.check.credit_rating')
        },
        closeAdditionalInfoModal () {
            this.showAdditionalInfoModal = false
            this.additionalInfoTitle = null
            this.additionalInfoForm = null
            this.additionalInfoInputType = null
            this.additionalInfoLabel = null
        },
        formatString (string) {
            if (string) {
                return string
            }

            return '/'
        },
        openSignedDocument (url) {
            window.open(url, '_blank')
        }
    }
}
</script>

<style lang="scss" scoped>
.order-check {
    display: flex;
    gap: 15px;
    flex-direction: column;

    & .overview {
        display: grid;
        grid-template-columns: 1fr auto;
        grid-gap: 20px;
        padding: 5px;

        & main {
            display: flex;
            grid-gap: 20px;

            & .more-line-comments {
                display: grid;
                grid-gap: 5px;
                width: 100%;

                & span:nth-child(2) {
                    padding-bottom: 10px;
                }
            }
            & .more-line-fourth {
                display: grid;
                grid-gap: 5px;
                width: 300px;

                & span:nth-child(2) {
                    padding-bottom: 10px;
                }
            }
        }
        & aside {
            display: flex;
            grid-gap: 20px;
            min-width: 130px;
            & .more-line {
                display: grid;
                grid-gap: 10px;

                & span:nth-child(2) {
                    padding-bottom: 10px;
                }
            }
        }
    }
    & .sticky-top {
        top: -30px;
        & .tabs-wrapper {
            background: var(--colorAppBase);
            border-radius: 5px;
            border: 1px solid var(--colorAppBorder);
        }
    }

    & .more-line {
        display: grid;
        grid-gap: 5px;
        min-width: 18%;

        & span:nth-child(2) {
            padding-bottom: 10px;
        }

        & .margin-top-8 {
            margin-top: 8px;
        }
    }

    & .more-line-big-width-second-bold {
        display: grid;
        grid-gap: 5px;
        min-width: 28%;

        & span:nth-child(2) {
            padding-bottom: 10px;
        }

        & span:nth-child(2) {
            font-weight: bold;
        }
    }

    & .more-line-big-width {
        display: grid;
        grid-gap: 5px;
        min-width: 28%;

        & span:nth-child(2) {
            padding-bottom: 10px;
        }
    }
}
.cursor-pointer {
    cursor: pointer;
}
</style>
